import { create } from 'zustand';
import { workingHoursApi } from '@api/api.ts';
import { CreateWorkingHoursRequest, UpdateWorkingHoursRequest, WorkingHoursResponse } from '@api/generated';

interface WorkingHoursState {
  workingHours: WorkingHoursResponse[];
  loading: boolean;
  error: string | null;

  // Actions
  fetchWorkingHours: (params?: {
    userId?: string;
    salonId?: string;
    weekday?: number;
    isActive?: boolean;
    page?: number;
    limit?: number;
  }) => Promise<void>;

  fetchWorkingHoursByUser: (userId: string) => Promise<WorkingHoursResponse[]>;

  fetchActiveWorkingHoursByUser: (userId: string) => Promise<WorkingHoursResponse[]>;

  createWorkingHours: (request: CreateWorkingHoursRequest) => Promise<WorkingHoursResponse>;

  updateWorkingHours: (id: string, request: UpdateWorkingHoursRequest) => Promise<WorkingHoursResponse>;

  deleteWorkingHours: (id: string) => Promise<void>;

  bulkDeleteWorkingHours: (ids: string[]) => Promise<void>;

  getWorkingHoursByWeekday: (userId: string, weekday: number) => WorkingHoursResponse | undefined;

  clearError: () => void;
}

export const useWorkingHoursStore = create<WorkingHoursState>((set, get) => {
  return {
    workingHours: [],
    loading: false,
    error: null,

    fetchWorkingHours: async (params = {}) => {
      set({ loading: true, error: null });
      try {
        const response = await workingHoursApi.getWorkingHours({
          page: params.page,
          limit: params.limit,
          userId: params.userId,
          salonId: params.salonId,
          weekday: params.weekday,
          isActive: params.isActive,
        });

        // The endpoint returns a paged structure; defensively unwrap.
        const data = (response.data as any)?.content ?? (response.data as any)?.data ?? response.data ?? [];
        set({
          workingHours: Array.isArray(data) ? data : [],
          loading: false,
        });
      } catch (error: any) {
        set({
          error: error.response?.data?.message || 'Chyba při načítání pracovních hodin',
          loading: false,
        });
      }
    },

    fetchWorkingHoursByUser: async (userId: string) => {
      set({ loading: true, error: null });
      try {
        const response = await workingHoursApi.getWorkingHoursByUser({ userId });
        set({ loading: false });
        return response.data;
      } catch (error: any) {
        set({
          error: error.response?.data?.message || 'Chyba při načítání pracovních hodin',
          loading: false,
        });
        throw error;
      }
    },

    fetchActiveWorkingHoursByUser: async (userId: string) => {
      set({ loading: true, error: null });
      try {
        const response = await workingHoursApi.getActiveWorkingHoursByUser({ userId });
        set({ loading: false });
        return response.data;
      } catch (error: any) {
        set({
          error: error.response?.data?.message || 'Chyba při načítání aktivních pracovních hodin',
          loading: false,
        });
        throw error;
      }
    },

    createWorkingHours: async (request: CreateWorkingHoursRequest) => {
      set({ loading: true, error: null });
      try {
        const response = await workingHoursApi.createWorkingHours({ createWorkingHoursRequest: request });
        set({ loading: false });

        // Refresh the list
        await get().fetchWorkingHours();

        return response.data;
      } catch (error: any) {
        set({
          error: error.response?.data?.message || 'Chyba při vytváření pracovních hodin',
          loading: false,
        });
        throw error;
      }
    },

    updateWorkingHours: async (id: string, request: UpdateWorkingHoursRequest) => {
      set({ loading: true, error: null });
      try {
        const response = await workingHoursApi.updateWorkingHours({
          id,
          updateWorkingHoursRequest: request,
        });
        set({ loading: false });

        // Update the local state
        const { workingHours } = get();
        const updatedHours = workingHours.map(wh => (wh.id === id ? response.data : wh));
        set({ workingHours: updatedHours });

        return response.data;
      } catch (error: any) {
        set({
          error: error.response?.data?.message || 'Chyba při aktualizaci pracovních hodin',
          loading: false,
        });
        throw error;
      }
    },

    deleteWorkingHours: async (id: string) => {
      set({ loading: true, error: null });
      try {
        await workingHoursApi.deleteWorkingHours({ id });

        // Remove from local state
        const { workingHours } = get();
        const filteredHours = workingHours.filter(wh => wh.id !== id);
        set({ workingHours: filteredHours, loading: false });
      } catch (error: any) {
        set({
          error: error.response?.data?.message || 'Chyba při mazání pracovních hodin',
          loading: false,
        });
        throw error;
      }
    },

    bulkDeleteWorkingHours: async (ids: string[]) => {
      set({ loading: true, error: null });
      try {
        await workingHoursApi.bulkDeleteWorkingHours({ requestBody: ids });

        // Remove from local state
        const { workingHours } = get();
        const filteredHours = workingHours.filter(wh => !ids.includes(wh.id));
        set({ workingHours: filteredHours, loading: false });
      } catch (error: any) {
        set({
          error: error.response?.data?.message || 'Chyba při hromadném mazání pracovních hodin',
          loading: false,
        });
        throw error;
      }
    },

    getWorkingHoursByWeekday: (userId: string, weekday: number) => {
      const { workingHours } = get();
      return workingHours.find(wh => wh.userId === userId && wh.weekday === weekday);
    },

    clearError: () => set({ error: null }),
  };
});
